import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Slot } from 'radix-ui'
import * as React from 'react'

const buttonVariants = cva(
  "inline-flex cursor-pointer shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-xs/relaxed font-medium whitespace-nowrap transition-all duration-100 outline-none focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 dark:aria-invalid:border-destructive/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 group/button select-none",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/80 active:bg-primary/60',
        primaryRaised:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/80 active:bg-primary/60',
        raised:
          'border-border bg-background text-muted-foreground shadow-xs hover:bg-accent hover:text-accent-foreground active:bg-accent/80',
        inputRaised:
          'border-input bg-input/30 text-foreground shadow-xs hover:bg-input/50 active:bg-input/80',
        /** The active browser-tab surface (`TabsTrigger variant='browser'`). */
        sidebarRaised:
          'border-input bg-sidebar text-foreground hover:bg-sidebar-accent active:bg-sidebar-accent/80 aria-expanded:bg-sidebar-accent',
        warningRaised:
          'border-amber-500/60 bg-amber-950/70 text-amber-100 shadow-xs hover:bg-amber-900/80 active:bg-amber-900 [&_svg]:text-amber-400',
        destructive:
          'bg-destructive/10 hover:bg-destructive/20 active:bg-destructive/30 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30',
        outline:
          'border-border dark:bg-input/30 hover:bg-input/50 dark:hover:bg-foreground/15 active:bg-input/80 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/60 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        inverse:
          'bg-foreground text-background hover:bg-foreground/85 active:bg-foreground/70 dark:bg-white dark:text-black dark:hover:bg-white/90 dark:active:bg-white/80',
        ghost:
          'hover:bg-foreground/5 active:bg-foreground/10 hover:text-foreground dark:hover:bg-foreground/8 dark:active:bg-foreground/16 aria-expanded:bg-muted aria-expanded:text-foreground',
        ghostLight:
          'hover:bg-transparent hover:text-foreground active:bg-transparent active:text-foreground aria-expanded:bg-transparent aria-expanded:text-foreground',
        browserTabAction:
          'relative z-10 bg-transparent text-muted-foreground hover:bg-accent/40 hover:text-foreground active:bg-accent/60 active:text-foreground',
        browserTabCloseAction:
          'relative z-10 rounded-sm bg-transparent text-muted-foreground/80 hover:bg-accent/50 hover:text-foreground active:bg-accent/60 active:text-foreground',
        ghostMuted:
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground active:bg-foreground/10 dark:hover:bg-foreground/8 dark:active:bg-foreground/16 focus-visible:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /** Toggle-button treatments, driven by the aria-pressed the caller already sets. */
      pressed: {
        none: '',
        invert:
          'aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background aria-pressed:hover:bg-foreground aria-pressed:hover:text-background aria-pressed:active:bg-foreground',
        accent:
          'aria-pressed:bg-accent aria-pressed:text-accent-foreground aria-pressed:hover:bg-accent aria-pressed:hover:text-accent-foreground',
        emphasis: 'text-muted-foreground aria-pressed:text-foreground',
      },
      tone: {
        default: '',
        overlay:
          'border-transparent bg-muted-foreground/60 text-foreground shadow-xs hover:bg-muted-foreground/75 hover:text-foreground active:bg-muted-foreground/85 active:text-foreground',
      },
      size: {
        default:
          "h-7 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        xs: "h-5 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-6 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-8 gap-1 px-2.5 text-xs/relaxed has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-xs': "size-5 rounded-sm [&_svg:not([class*='size-'])]:size-2.5",
        'icon-sm': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-lg': "size-8 [&_svg:not([class*='size-'])]:size-4",
        'icon-xl': "size-9 [&_svg:not([class*='size-'])]:size-5",
        'tab-icon':
          "h-8 w-8 shrink-0 rounded-md px-0 [&_svg:not([class*='size-'])]:size-4",
        toolbar:
          "h-6 gap-1.5 px-2 text-xs/4 [&_svg:not([class*='size-'])]:size-4",
        inline:
          'h-auto min-h-0 w-fit justify-start gap-0 self-start rounded-none p-0 font-normal shadow-none hover:bg-transparent active:bg-transparent dark:hover:bg-transparent has-data-[icon=inline-end]:gap-1 has-data-[icon=inline-end]:p-0 has-data-[icon=inline-start]:gap-1 has-data-[icon=inline-start]:p-0',
      },
      shape: {
        default: '',
        round: 'rounded-full',
      },
      iconSize: {
        default: '',
        sm: "[&_svg:not([class*='size-'])]:size-3",
      },
      width: {
        auto: '',
        full: 'w-full min-w-0',
      },
      gap: {
        default: '',
        none: 'gap-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      tone: 'default',
      shape: 'default',
      size: 'default',
      iconSize: 'default',
      width: 'auto',
      gap: 'default',
      pressed: 'none',
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(
  (
    {
      className,
      variant = 'default',
      tone = 'default',
      shape = 'default',
      size = 'default',
      iconSize = 'default',
      width = 'auto',
      gap = 'default',
      pressed = 'none',
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'button'

    return (
      <Comp
        data-slot='button'
        data-variant={variant}
        data-size={size}
        data-shape={shape}
        className={cn(
          buttonVariants({
            variant,
            tone,
            shape,
            size,
            iconSize,
            width,
            gap,
            pressed,
            className,
          })
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
